/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { SetStateAction, useCallback, useMemo, useState } from 'react'
import usePaginationQuery from '../../../../client/js/components/pagination/usePaginationQuery'
import { useQuery } from '@tanstack/react-query'
import { deleteLease, findManyLeases } from '../../lib/rest/leaseAPIClient'
import { getHalEmbedded, getHalPageTotalElements } from '@jalik/hal'
import { QueryParams } from '../../../../client/js/hooks/useQueryParams'
import useSelection from '../../../../client/js/components/selection/useSelection'
import RequirePerm from '../../../auth/components/RequirePerm'
import { FormattedMessage, useIntl } from 'react-intl'
import SearchForm from '../../../../client/js/components/search/SearchForm'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSyncAlt } from '@fortawesome/free-solid-svg-icons/faSyncAlt'
import { faFilter } from '@fortawesome/free-solid-svg-icons/faFilter'
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash'
import { faPencilRuler } from '@fortawesome/free-solid-svg-icons/faPencilRuler'
import Filters from '../../../../client/js/components/Filters'
import LeaseStatusFilter from '../filters/LeaseStatusFilter'
import ManagedFilter from '../filters/ManagedFilter'
import FurnishedFilter from '../filters/FurnishedFilter'
import EquippedFilter from '../filters/EquippedFilter'
import WithChargesFilter from '../filters/WithChargesFilter'
import MissingFilesFilter from '../filters/MissingFilesFilter'
import MissingDataFilter from '../filters/MissingDataFilter'
import LeaseListAlerts from './LeaseListAlerts'
import SelectionAlert from '../../../../client/js/components/selection/SelectionAlert'
import LoadingHelper from '../../../../client/js/components/LoadingHelper'
import LeaseList from './LeaseList'
import DeleteSelectionModal from '../../../../client/js/components/modals/DeleteSelectionModal'
import ListCard from '../../../../client/js/components/ListCard'
import { getLeaseCreateLink } from '../../lib/links/leaseLinks'
import { getLeaseTemplateListLink } from '../../lib/links/leaseTemplateLinks'
import Filter from '../Filter'
import CustomerInput from '../inputs/CustomerInput'
import { handleValueChange } from '../../../../client/js/lib/form'
import { Button } from '@mantine/core'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import Toolbar from '../../../../client/js/components/Toolbar'

export type LeaseListQuery = {
  limit?: string,
  page?: string,
  search?: string,
  estateId?: string,
  customerId?: string,
  status?: string[],
  managed?: string,
  furnished?: string,
  equipped?: string,
  withCharges?: string,
  missingFiles?: string,
  missingData?: string,
}

export type LeaseListCardProps = {
  createLink?: string;
  onQueryChange (query: SetStateAction<LeaseListQuery>): void;
  query: LeaseListQuery;
}

const initialQuery: LeaseListQuery = {
  limit: '10',
  page: '1'
}

function LeaseListCard (props: LeaseListCardProps) {
  const {
    createLink = getLeaseCreateLink(),
    onQueryChange,
    query
  } = props

  const intl = useIntl()
  const [totalElements, setTotalElements] = useState<number>(0)
  const pagination = usePaginationQuery(totalElements)
  const { page, limit } = pagination

  const queryParams = useMemo(() => {
    return {
      ...initialQuery,
      ...query,
      page,
      limit
    }
  }, [limit, page, query])

  // Items loading.
  const listQuery = useQuery({
    enabled: true,
    queryKey: ['leases', queryParams],
    queryFn: async () => findManyLeases(queryParams).then((r) => {
      setTotalElements(getHalPageTotalElements(r))
      return r
    })
  })
  const items = useMemo(() => (
    (listQuery.data != null ? getHalEmbedded(listQuery.data)?.leases : null) || []
  ), [listQuery.data])

  const setFilter = useCallback((name: string, value?: unknown) => {
    onQueryChange((s: QueryParams) => ({ ...s, page: '1', [name]: value }))
  }, [onQueryChange])

  const setSearch = useCallback((search: string) => {
    setFilter('search', search)
  }, [setFilter])

  // Items selection.
  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const selection = useSelection<number>({
    multiple: true,
    items: selectedItems,
    onChange: setSelectedItems,
    source: items.map((el) => el.id)
  })

  // Items deletion.
  const handleDelete = useCallback((id: number) => deleteLease(id), [])
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const toggleDeleteModal = useCallback(() => {
    setIsDeleteModalOpen((s) => !s)
  }, [])

  // Items filtering.
  const [isFiltersVisible, setIsFiltersVisible] = useState(true)
  const toggleFilters = useCallback(() => {
    setIsFiltersVisible((s) => !s)
  }, [])

  return (
    <ListCard
      filters={<Filters in={isFiltersVisible}>
        <LeaseStatusFilter
          onChange={setFilter}
          value={queryParams.status}
        />
        <ManagedFilter
          onChange={setFilter}
          value={queryParams.managed}
        />
        <FurnishedFilter
          onChange={setFilter}
          value={queryParams.furnished}
        />
        <EquippedFilter
          onChange={setFilter}
          value={queryParams.equipped}
        />
        <WithChargesFilter
          onChange={setFilter}
          value={queryParams.withCharges}
        />
        <div>
          <MissingFilesFilter
            onChange={setFilter}
            value={queryParams.missingFiles}
          />
          <MissingDataFilter
            onChange={setFilter}
            value={queryParams.missingData}
          />
        </div>
        <Filter>
          <CustomerInput
            onChange={handleValueChange('customerId', setFilter)}
            value={queryParams.customerId}
          />
        </Filter>
      </Filters>}
      pagination={pagination}
      selection={selection}
      selectionActions={<SelectionAlert>
        <RequirePerm
          permissions={['write:lease']}
          render={() => (
            <Button onClick={toggleDeleteModal}>
              <FontAwesomeIcon
                fixedWidth
                icon={faTrash}
              />
            </Button>
          )}
        />
      </SelectionAlert>}
      toolbar={<Toolbar>
        <RequirePerm
          permissions={['write:lease']}
          render={() => (
            <Button
              variant="filled"
              component={AnchorLink}
              to={createLink}
            >
              <FormattedMessage
                id="create_rental_file"
                defaultMessage="Créer un dossier de location"
              />
            </Button>
          )}
        />
        <SearchForm
          initialValue={queryParams.search}
          onSubmit={setSearch}
          submitting={listQuery.isFetching}
        />
        <Button
          loading={listQuery.isFetching}
          onClick={() => {
            listQuery.refetch()
          }}
        >
          <FontAwesomeIcon
            fixedWidth
            icon={faSyncAlt}
          />
        </Button>
        <Button
          // todo active={isFiltersVisible}
          onClick={toggleFilters}
        >
          <FontAwesomeIcon
            fixedWidth
            icon={faFilter}
          />
        </Button>
        <RequirePerm
          permissions={[
            'read:lease-template',
            'write:lease-template'
          ]}
          render={() => (
            <Button
              component={AnchorLink}
              to={getLeaseTemplateListLink()}
              title={intl.formatMessage({
                id: 'lease_templates',
                defaultMessage: 'Modèles de bail'
              })}
            >
              <FontAwesomeIcon
                fixedWidth
                icon={faPencilRuler}
              />
            </Button>
          )}
        />
      </Toolbar>}
    >
      <LeaseListAlerts />

      <LoadingHelper
        query={listQuery}
        render={() => (
          <LeaseList items={items} />
        )}
      />

      {isDeleteModalOpen
        ? <DeleteSelectionModal
          opened={isDeleteModalOpen}
          items={selection.items}
          onClose={toggleDeleteModal}
          onConfirm={handleDelete}
          onSuccess={() => {
            selection.clear()
            listQuery.refetch()
          }}
          requirePin
        >
          <ul>
            <li>
              {intl.formatMessage({
                id: 'invoices_will_be_deleted',
                defaultMessage: 'Les factures seront supprimées'
              })}
            </li>
            <li>
              {intl.formatMessage({
                id: 'expenses_and_revenues_will_be_deleted',
                defaultMessage: 'Les dépenses et revenus seront supprimés'
              })}
            </li>
            <li>
              {intl.formatMessage({
                id: 'management_reports_will_be_deleted',
                defaultMessage: 'Les rapports de gérance seront supprimés'
              })}
            </li>
            <li>
              {intl.formatMessage({
                id: 'interventions_will_be_deleted',
                defaultMessage: 'Les interventions seront supprimées'
              })}
            </li>
          </ul>
        </DeleteSelectionModal>
        : null}
    </ListCard>
  )
}

export default LeaseListCard
